// src/contexts/ModelContext.tsx

import React, { createContext, useContext, useState, useEffect } from "react";
import { ModelService, ModelRequest, StreamCallbacks } from "../interfaces/modelService";
import { NormalizedCatylexSearchResult, CatylexToolInput } from "../interfaces/catylexSearchTypes";
import { createLogger } from "../utils/logger";
import { useAgent } from "./AgentContext";
import { agentModelServiceFactory } from "../services/agentModelServiceFactory";
import { useSession } from "./SessionContext";
import { AgentAwareModelService } from "../services/agentAwareModelService";
import { WebCitationInsertion } from "../interfaces/webCitationTypes";

const log = createLogger("ModelContext");

/**
 * Context properties exposed to components
 *
 * Enhanced with session management coordination for robust conversation handling
 * and future-ready access to enhanced features like web search data
 */
interface ModelContextType {
  // Streaming API access
  generateStreamingResponse: (request: ModelRequest, callbacks: StreamCallbacks) => Promise<void>;

  // Cancel ongoing generation
  cancelGeneration: () => void;

  // Session management coordination
  clearSession: () => void;

  // Status information
  isGenerating: boolean;

  // Check if a specific agent is generating (for parallel agent support)
  isGeneratingForAgent: (agentId: string) => boolean;

  // Service information for debugging (optional functionality)
  getServiceInfo: () => any;

  // Enhanced features access for future UI implementation
  getWebSearchData: () => { queries: string[]; results: any[] };
}

// Create the context with undefined default value
const ModelContext = createContext<ModelContextType | undefined>(undefined);

/**
 * Props for the ModelProvider component
 */
interface ModelProviderProps {
  children: React.ReactNode;
  // Optional prop to inject a different model service for testing
  modelService?: ModelService;
}

/**
 * Provider component for model service access
 *
 * This provider has been updated to use your chat model service by default
 * while maintaining the same interface that your existing components expect.
 * The beauty of this approach is that none of your UI components need to change.
 *
 * Enhanced with session coordination and improved error handling.
 */
export const ModelProvider: React.FC<ModelProviderProps> = ({
  children,
  modelService: externalModelService,
}) => {
  // Get current agent from context - this is the key addition
  const { currentAgent } = useAgent();

  // Get contract type from session context
  const { getContractType } = useSession();

  // Store the current agent ID in a ref to track changes
  const currentAgentIdRef = React.useRef(currentAgent.id);

  // Create the appropriate model service - now agent-aware
  const [modelService, setModelService] = useState<ModelService>(() => {
    // If an external service is provided (useful for testing), use it
    if (externalModelService) {
      log.info("Using externally provided model service", {
        serviceType: externalModelService.constructor.name,
      });
      return externalModelService;
    }

    // Use the factory to get an agent-specific service
    log.info("Creating agent-specific model service", {
      agentId: currentAgent.id,
      agentName: currentAgent.name,
    });
    return agentModelServiceFactory.getServiceForAgent(currentAgent.id);
  });

  // Track generation state per agent for parallel processing
  const [generatingStates, setGeneratingStates] = useState<Map<string, boolean>>(new Map());

  useEffect(() => {
    // Only react to agent changes if we're not using an external service
    if (!externalModelService && currentAgent.id !== currentAgentIdRef.current) {
      log.info("Agent changed, updating model service", {
        previousAgent: currentAgentIdRef.current,
        newAgent: currentAgent.id,
      });

      // The factory will handle cleanup of the old service
      // Don't reset generating state - let each agent continue independently

      // Get the new service for the new agent
      const newService = agentModelServiceFactory.getServiceForAgent(currentAgent.id);
      setModelService(newService);

      // Sync contract type from session to the new service
      const contractType = getContractType();
      if (contractType && newService && "setContractType" in newService) {
        (newService as AgentAwareModelService).setContractType(contractType);
        log.info("Contract type synced to new agent service", {
          contractType,
          newAgent: currentAgent.id,
        });
      }

      // Update our ref to track the current agent
      currentAgentIdRef.current = currentAgent.id;
    }
  }, [currentAgent.id, externalModelService, getContractType]);

  /**
   * Generate a streaming response with state tracking
   *
   * This method wraps the service call with state management to provide
   * consistent loading states across your UI components.
   * Enhanced with conversation history tracking for debugging capabilities.
   */
  const generateStreamingResponse = async (
    request: ModelRequest,
    callbacks: StreamCallbacks
  ): Promise<void> => {
    log.info("Starting streaming response generation", {
      promptLength: request.prompt.length,
      promptPreview: request.prompt.substring(0, 50) + (request.prompt.length > 50 ? "..." : ""),
      hasDocumentContext: !!request.documentContext,
      conversationLength: request.conversationHistory?.length || 0,
      hasThinkingCallbacks: !!(callbacks.onThinkingStart || callbacks.onThinkingComplete),
    });

    // Create wrapped callbacks that manage the generation state per agent
    const wrappedCallbacks: StreamCallbacks = {
      onStart: () => {
        log.debug("Setting isGenerating to true for agent", { agentId: currentAgent.id });
        setGeneratingStates((prev) => new Map(prev).set(currentAgent.id, true));
        callbacks.onStart();
      },

      onChunk: (chunk: string) => {
        // Pass through chunk without state changes for performance
        callbacks.onChunk(chunk);
      },

      onComplete: (response) => {
        log.info("Streaming completed successfully", {
          agentId: currentAgent.id,
          responseLength: response.content.length,
          sessionId: response.metadata?.sessionId,
          hadThinking: response.metadata?.thinkingContent ? true : false,
          thinkingDuration: response.metadata?.thinkingDuration,
        });
        setGeneratingStates((prev) => new Map(prev).set(currentAgent.id, false));
        callbacks.onComplete(response);
      },

      onError: (error) => {
        log.error("Streaming error occurred", {
          agentId: currentAgent.id,
          error: error.message,
          errorType: error.constructor.name,
        });
        setGeneratingStates((prev) => new Map(prev).set(currentAgent.id, false));
        callbacks.onError(error);
      },

      // CRITICAL FIX: Pass through the optional thinking callbacks
      // These were missing, causing the thinking UI to not appear during the thinking phase
      onThinkingStart: callbacks.onThinkingStart
        ? () => {
            log.debug("Thinking started - passing through to UI callbacks");
            callbacks.onThinkingStart!();
          }
        : undefined,

      onThinkingChunk: callbacks.onThinkingChunk
        ? (chunk: string) => {
            callbacks.onThinkingChunk!(chunk);
          }
        : undefined,

      onThinkingComplete: callbacks.onThinkingComplete
        ? (thinkingContent: string, signature: string | null) => {
            log.debug("Thinking completed - passing through to UI callbacks", {
              thinkingContentLength: thinkingContent.length,
              hasSignature: signature !== null,
            });
            callbacks.onThinkingComplete!(thinkingContent, signature);
          }
        : undefined,

      // CRITICAL FIX: Pass through the optional web search callbacks
      // These were missing, causing the web search UI to not appear
      onWebSearchToolUse: callbacks.onWebSearchToolUse
        ? (toolInfo: any) => {
            log.debug("Web search tool use - passing through to UI callbacks");
            callbacks.onWebSearchToolUse!(toolInfo);
          }
        : undefined,

      onWebSearchQuery: callbacks.onWebSearchQuery
        ? (query: any) => {
            log.debug("Web search query - passing through to UI callbacks", {
              query: query.query,
            });
            callbacks.onWebSearchQuery!(query);
          }
        : undefined,

      onWebSearchResults: callbacks.onWebSearchResults
        ? (results: any[]) => {
            log.debug("Web search results - passing through to UI callbacks", {
              resultCount: results.length,
            });
            callbacks.onWebSearchResults!(results);
          }
        : undefined,

      // Pass through the optional web fetch callback
      onWebFetchResult: callbacks.onWebFetchResult
        ? (result: any) => {
            log.debug("Web fetch result - passing through to UI callbacks", {
              url: result.url,
              status: result.status,
            });
            callbacks.onWebFetchResult!(result);
          }
        : undefined,

      // Pass through auto-redline callbacks
      onAutoRedlineToolResult: callbacks.onAutoRedlineToolResult
        ? (result: any) => {
            log.debug("Auto-redline tool result - passing through to UI callbacks", {
              sectionName: result.sectionName,
              editsCount: result.edits?.length,
            });
            callbacks.onAutoRedlineToolResult!(result);
          }
        : undefined,

      // Pass through add-comment callbacks
      onAddCommentToolResult: callbacks.onAddCommentToolResult
        ? (result: any) => {
            log.debug("Add-comment tool result - passing through to UI callbacks", {
              sectionName: result.sectionName,
              paragraphId: result.paragraphId,
            });
            callbacks.onAddCommentToolResult!(result);
          }
        : undefined,

      onToolUseStart: callbacks.onToolUseStart
        ? (toolName: string) => {
            log.debug("Tool use start - passing through to UI callbacks", { toolName });
            callbacks.onToolUseStart!(toolName);
          }
        : undefined,

      // Backend LLM-analytics trace id for this turn. Every callback has to be
      // relisted here explicitly - this object is built key by key, so one that
      // is merely present on the caller's object is silently dropped.
      onAiTraceId: callbacks.onAiTraceId
        ? (aiTraceId: string) => {
            callbacks.onAiTraceId!(aiTraceId);
          }
        : undefined,

      // Pass through form request callbacks
      onFormRequest: callbacks.onFormRequest
        ? (payload: any) => {
            log.debug("Form request - passing through to UI callbacks", {
              formType: payload.formType,
              formInstanceId: payload.formInstanceId,
              fieldCount: payload.fields?.length,
            });
            callbacks.onFormRequest!(payload);
          }
        : undefined,

      // Pass through playbook tool callbacks
      onPlaybookToolResult: callbacks.onPlaybookToolResult
        ? (result: any) => {
            log.debug("Playbook tool result passing through", {
              contentLength: result.content_length,
            });
            callbacks.onPlaybookToolResult!(result);
          }
        : undefined,

      // Pass through playbook selection card callbacks
      onPlaybookSelectionRequest: callbacks.onPlaybookSelectionRequest
        ? (result: any) => {
            log.debug("Playbook selection card passing through", {
              playbookCount: result.playbooks?.length,
            });
            callbacks.onPlaybookSelectionRequest!(result);
          }
        : undefined,

      // Pass through Catylex search callbacks
      onCatylexSearchResult: callbacks.onCatylexSearchResult
        ? (result: NormalizedCatylexSearchResult) => {
            log.debug("Catylex search result passing through", {
              status: result.status,
              searchType: result.searchType,
            });
            callbacks.onCatylexSearchResult!(result);
          }
        : undefined,

      onCatylexToolInput: callbacks.onCatylexToolInput
        ? (input: CatylexToolInput) => {
            log.debug("Catylex tool input passing through", {
              action: input.action,
            });
            callbacks.onCatylexToolInput!(input);
          }
        : undefined,

      // Pass through SharePoint search callbacks
      onSharePointSearchResult: callbacks.onSharePointSearchResult
        ? (result: any) => {
            log.debug("SharePoint search result passing through", {
              action: result.action,
              status: result.status,
            });
            callbacks.onSharePointSearchResult!(result);
          }
        : undefined,

      onSharePointToolInput: callbacks.onSharePointToolInput
        ? (input: any) => {
            log.debug("SharePoint tool input passing through", {
              action: input.action,
            });
            callbacks.onSharePointToolInput!(input);
          }
        : undefined,

      // Pass through document citation callbacks
      onDocumentCitation: callbacks.onDocumentCitation
        ? (citation: any) => {
            callbacks.onDocumentCitation!(citation);
          }
        : undefined,

      // Pass through web citation insertions
      onWebCitations: callbacks.onWebCitations
        ? (insertions: WebCitationInsertion[]) => {
            callbacks.onWebCitations!(insertions);
          }
        : undefined,

      // Pass through Outlook mail callbacks
      onOutlookMailResult: callbacks.onOutlookMailResult
        ? (result: any) => {
            log.debug("Outlook mail result passing through", {
              action: result.action,
              status: result.status,
            });
            callbacks.onOutlookMailResult!(result);
          }
        : undefined,

      onOutlookMailToolInput: callbacks.onOutlookMailToolInput
        ? (input: any) => {
            log.debug("Outlook mail tool input passing through", {
              action: input.action,
            });
            callbacks.onOutlookMailToolInput!(input);
          }
        : undefined,
    };

    try {
      // Delegate to the service layer
      await modelService.generateStreamingResponse(request, wrappedCallbacks);
    } catch (error) {
      // Ensure state is reset even if service throws synchronously
      log.error("Synchronous error in generateStreamingResponse", {
        error: error instanceof Error ? error.message : String(error),
      });
      setGeneratingStates((prev) => new Map(prev).set(currentAgent.id, false));
      throw error;
    }
  };

  // Get the current agent's generating state
  const isGenerating = generatingStates.get(currentAgent.id) || false;

  /**
   * Check if a specific agent is currently generating
   * This enables parallel agent cancellation detection in useChatAnalysis
   */
  const isGeneratingForAgent = (agentId: string): boolean => {
    return generatingStates.get(agentId) || false;
  };

  /**
   * Cancel ongoing generation
   *
   * This method provides a clean way to cancel streaming requests,
   * which is especially important for chat requests that might
   * involve file uploads and longer processing times.
   */
  const cancelGeneration = (): void => {
    log.info("Cancelling ongoing generation", {
      agentId: currentAgent.id,
      wasGenerating: isGenerating,
      hasServiceCancelMethod: !!modelService.cancelStreaming,
    });

    if (modelService.cancelStreaming) {
      modelService.cancelStreaming();
    }

    // Reset state immediately when cancelled
    setGeneratingStates((prev) => new Map(prev).set(currentAgent.id, false));
  };

  /**
   * Clear session with proper coordination
   *
   * This method ensures both React-side and API-side sessions are cleared together,
   * preventing session ID mismatch issues that could cause conversation history problems.
   */
  const clearSession = (): void => {
    log.info("Clearing model service session", {
      isGenerating: isGenerating,
      serviceType: modelService.constructor.name,
      currentAgent: currentAgent.id,
    });

    try {
      // First, cancel any ongoing generation
      if (isGenerating) {
        cancelGeneration();
      }

      // Clear the service session if the method exists
      if (modelService.clearSession) {
        modelService.clearSession();
        log.debug("Model service session cleared successfully", {
          agentId: currentAgent.id,
        });
      } else {
        log.warn("Model service does not have clearSession method", {
          serviceType: modelService.constructor.name,
        });
      }

      // Reset local state for current agent
      setGeneratingStates((prev) => new Map(prev).set(currentAgent.id, false));
    } catch (error) {
      log.error("Error clearing model service session", {
        error: error instanceof Error ? error.message : String(error),
        agentId: currentAgent.id,
      });
    }
  };

  /**
   * Get service information for debugging and monitoring
   *
   * This method provides insight into the current service state,
   * which is useful for debugging and monitoring in development.
   * Enhanced with better error handling to prevent undefined property access.
   */
  const getServiceInfo = (): any => {
    try {
      // Check if the service has getSessionInfo method with proper error handling
      if (
        modelService &&
        typeof modelService === "object" &&
        "getSessionInfo" in modelService &&
        typeof modelService.getSessionInfo === "function"
      ) {
        const serviceMethod = modelService.getSessionInfo as () => any;
        const serviceInfo = serviceMethod();

        return {
          serviceType: "Chat",
          isGenerating,
          ...serviceInfo,
        };
      } else {
        // Graceful fallback when getSessionInfo is not available
        return {
          serviceType: modelService?.constructor?.name || "Unknown",
          isGenerating,
          sessionInfo: "Method not available",
        };
      }
    } catch (error) {
      // Handle any errors gracefully without disrupting the application
      log.warn("Could not retrieve service information", {
        error: error instanceof Error ? error.message : String(error),
      });

      return {
        serviceType: "Error",
        isGenerating,
        error: error instanceof Error ? error.message : String(error),
      };
    }
  };

  /**
   * Get accumulated web search data for future UI implementation
   *
   * This method provides access to web search queries and results that have been
   * collected during the current session. When you're ready to implement web search
   * UI features, this data will be readily available.
   */
  const getWebSearchData = (): { queries: string[]; results: any[] } => {
    try {
      // Check if the service has getWebSearchData method (enhanced chat service feature)
      if (
        modelService &&
        typeof modelService === "object" &&
        "getWebSearchData" in modelService &&
        typeof modelService.getWebSearchData === "function"
      ) {
        const serviceMethod = modelService.getWebSearchData as () => {
          queries: string[];
          results: any[];
        };
        return serviceMethod();
      } else {
        // Graceful fallback for services that don't support web search
        log.debug("Service does not support web search data access", {
          serviceType: modelService?.constructor?.name || "Unknown",
        });
        return { queries: [], results: [] };
      }
    } catch (error) {
      // Handle any errors gracefully
      log.warn("Could not retrieve web search data", {
        error: error instanceof Error ? error.message : String(error),
      });
      return { queries: [], results: [] };
    }
  };

  // Create the context value with all required methods
  const contextValue: ModelContextType = {
    generateStreamingResponse,
    cancelGeneration,
    clearSession,
    isGenerating,
    isGeneratingForAgent,
    getServiceInfo,
    getWebSearchData,
  };

  return <ModelContext.Provider value={contextValue}>{children}</ModelContext.Provider>;
};

/**
 * Custom hook to use the model context
 */
export const useModel = (): ModelContextType => {
  const context = useContext(ModelContext);

  if (context === undefined) {
    throw new Error(
      "useModel must be used within a ModelProvider. " +
        "Make sure your component is wrapped with <ModelProvider>."
    );
  }

  return context;
};
